import { DataFeedRowData } from '../../../types/generatedTypes';

export interface UploadError {
  response?: {
    data?:
      | {
          message?: string;
          api_error?:
            | {
                api_error?: string;
                error_type?: string;
                instrument_identifier?: string;
                instrument_type?: string;
                resolution_status?: string;
              }
            | string;
          error_classification?: {
            category: string;
            title: string;
            message: string;
            severity: 'error' | 'warning' | 'info';
            suggestion?: string;
            action_required?: string;
            technical_details?: string;
            affected_field?: string;
            field_value?: string;
            field_suggestions?: string[];
          };
          details?: {
            resolution_status?: string;
            available_instruments?: Array<{
              sourceId?: string | { sourceId?: string };
              name?: string;
              currency?: string;
              type?: string;
              description?: string;
            }>;
          };
        }
      | string;
    status?: number;
  };
  status?: number;
  message?: string;
}

export interface ErrorClassification {
  category: string;
  title: string;
  message: string;
  severity: 'error' | 'warning' | 'info';
  affected_field?: string;
  field_value?: string;
  field_suggestions?: string[];
  available_instruments?: Array<{
    sourceId?: string | { sourceId?: string };
    name?: string;
    currency?: string;
    type?: string;
    description?: string;
  }>;
  detailed_message?: string;
}

export const getCellError = (
  rowId: number,
  fieldName: string,
  uploadErrors: Record<number, UploadError>,
  rows: DataFeedRowData[]
): ErrorClassification | null => {
  const error = uploadErrors[rowId];
  const row = rows.find(r => r.id === rowId);

  // Check for in-memory error classification structure first
  if (error) {
    const responseData = error?.response?.data;
    if (
      responseData &&
      typeof responseData === 'object' &&
      'error_classification' in responseData
    ) {
      const errorClassification = responseData.error_classification;
      if (
        errorClassification &&
        errorClassification.affected_field === fieldName
      ) {
        return errorClassification;
      }
    }

    // Check for detailed error responses (multiple instruments, etc.)
    if (responseData && typeof responseData === 'object') {
      const apiError = responseData.api_error;
      const details = responseData.details;

      // Handle instrument-related errors for Symbol field
      if (fieldName === 'Symbol' && (apiError || details)) {
        const message = responseData.message || '';
        const apiErrorText = typeof apiError === 'string' ? apiError : '';

        // Check for multiple instruments error
        if (
          apiErrorText.toLowerCase().includes('multiple instruments') ||
          details?.resolution_status?.includes('multiple')
        ) {
          const availableInstruments = details?.available_instruments || [];
          const instrumentNames = availableInstruments
            .map(
              (inst: {
                sourceId?: string | { sourceId?: string };
                name?: string;
                currency?: string;
              }) => {
                const sourceIdValue =
                  typeof inst.sourceId === 'object' && inst.sourceId?.sourceId
                    ? inst.sourceId.sourceId
                    : typeof inst.sourceId === 'string'
                      ? inst.sourceId
                      : 'N/A';
                return `${inst.name || 'Unknown'} (${sourceIdValue}) - ${inst.currency || 'N/A'}`;
              }
            )
            .join(', ');

          return {
            category: 'multiple_instruments',
            title: 'Multiple Instruments Found',
            message: `Found ${availableInstruments.length} matching instruments: ${instrumentNames}`,
            severity: 'info' as const,
            affected_field: fieldName,
            field_value: row?.[fieldName],
            field_suggestions: [
              'Use exact ISIN',
              'Specify currency',
              'Use full instrument name',
            ],
            available_instruments: availableInstruments,
            detailed_message: apiErrorText,
          };
        }

        // Check for no instruments found error
        if (
          apiErrorText.toLowerCase().includes('no instruments found') ||
          details?.resolution_status === 'not_found'
        ) {
          return {
            category: 'instrument_not_found',
            title: 'Instrument Not Found',
            message:
              apiErrorText || message || 'No instruments found for this symbol',
            severity: 'error' as const,
            affected_field: fieldName,
            field_value: row?.[fieldName],
            field_suggestions: [
              'Check symbol spelling',
              'Try ISIN instead',
              'Verify instrument type',
              'Check if instrument exists in Illio',
            ],
          };
        }
      }
    }
  }

  // Check in the row data for upload_status errors (database-persisted errors)
  if (row?.upload_status?.status === 'error') {
    const uploadStatus = row.upload_status;

    // Map instrument-related errors to Symbol field
    if (fieldName === 'Symbol') {
      const message = uploadStatus.message || '';
      const isInstrumentError =
        message.toLowerCase().includes('instrument') ||
        message.toLowerCase().includes('symbol') ||
        message.includes('could not be found in Illio') ||
        message.includes("field 'Symbol'");

      if (isInstrumentError) {
        return {
          category: 'instrument_not_found',
          title: 'Instrument Error',
          message: message,
          severity: 'error' as const,
          affected_field: fieldName,
          field_value: row[fieldName],
          field_suggestions: [
            'Check symbol spelling',
            'Try using ISIN instead',
            'Verify instrument exists in Illio',
          ],
        };
      }
    }

    // Map currency-related errors to currency field
    if (
      fieldName.toLowerCase().includes('מטבע') ||
      fieldName.toLowerCase().includes('currency')
    ) {
      const message = uploadStatus.message || '';
      const isCurrencyError =
        message.toLowerCase().includes('currency') ||
        message.toLowerCase().includes('מטבע');

      if (isCurrencyError) {
        return {
          category: 'currency_error',
          title: 'Currency Error',
          message: message,
          severity: 'warning' as const,
          affected_field: fieldName,
          field_value: row[fieldName],
          field_suggestions: [
            'Check currency code',
            'Verify currency is supported',
          ],
        };
      }
    }
  }

  return null;
};
